import { gql } from 'graphql-request';
import { AxiosRequestConfig } from 'axios';
import { getGraphQLQueryClient } from './query-client';
import axiosClient from './request-client';
import type { Nullable, UserWithToken, WithData } from './api.types';

/**
 * Fundas del overlay de videos y streams: un asset del sistema que sube un
 * admin. Las públicas las elige cualquiera; el resto, a quien se le otorgó.
 * En el overlay gana la del jugador en el torneo, después la del torneo y
 * después la que eligió el jugador.
 */
export type Sleeve = {
  id: number;
  name: string;
  image: string;
  imageUrl: string;
  isPublic: boolean;
  usersCount: number;
  tournamentsCount: number;
  tournamentPlayersCount: number;
  grantsCount: number;
};

/** Una funda que el jugador puede elegir: pública u otorgada (con motivo). */
export type AvailableSleeve = {
  id: number;
  name: string;
  imageUrl: string;
  isPublic: boolean;
  reason: Nullable<string>;
  grantedAt: Nullable<string>;
};

export type PlayerSleeves = {
  selectedSleeveId: Nullable<number>;
  sleeves: AvailableSleeve[];
};

const SLEEVE_FIELDS = `
  id
  name
  image
  imageUrl
  isPublic
  usersCount
  tournamentsCount
  tournamentPlayersCount
  grantsCount
`;

const PLAYER_SLEEVES_FIELDS = `
  selectedSleeveId
  sleeves {
    id
    name
    imageUrl
    isPublic
    reason
    grantedAt
  }
`;

/** La biblioteca, por nombre. Admin. */
export const getSleeves = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<Sleeve[]>>(
    gql`
      query sleeves {
        data: sleeves {
          ${SLEEVE_FIELDS}
        }
      }
    `
  );

/**
 * Alta de una funda: imagen + nombre. Va por REST porque es multipart y
 * `/graphql` sólo acepta JSON. Admin.
 */
export const uploadSleeve = (name: string, file: File, config?: AxiosRequestConfig) => {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('image', file);
  return axiosClient.post('/sleeves', formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
  });
};

export const renameSleeve = async (
  user: Nullable<UserWithToken>,
  id: number,
  name: string
) =>
  getGraphQLQueryClient(user).request<WithData<Sleeve>>(
    gql`
      mutation renameSleeve($id: Int!, $name: String!) {
        data: renameSleeve(id: $id, name: $name) {
          ${SLEEVE_FIELDS}
        }
      }
    `,
    { id, name }
  );

/** Los jugadores y torneos que la tenían quedan sin funda. */
export const deleteSleeve = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteSleeve($id: Int!) {
        data: deleteSleeve(id: $id)
      }
    `,
    { id }
  );

/** Como setMySleeve, para otro jugador: sólo una que tenga disponible. Admin. */
export const setUserSleeve = async (
  user: Nullable<UserWithToken>,
  userId: number,
  sleeveId: Nullable<number>
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation setUserSleeve($userId: Int!, $sleeveId: Int) {
        data: setUserSleeve(userId: $userId, sleeveId: $sleeveId)
      }
    `,
    { userId, sleeveId }
  );

/** Publicarla o no. Despublicarla se la saca a quien no la tiene otorgada. Admin. */
export const setSleevePublic = async (
  user: Nullable<UserWithToken>,
  id: number,
  isPublic: boolean
) =>
  getGraphQLQueryClient(user).request<WithData<Sleeve>>(
    gql`
      mutation setSleevePublic($id: Int!, $isPublic: Boolean!) {
        data: setSleevePublic(id: $id, isPublic: $isPublic) {
          ${SLEEVE_FIELDS}
        }
      }
    `,
    { id, isPublic }
  );

/** Las que puede elegir el usuario logueado, y la elegida. */
export const getMySleeves = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<PlayerSleeves>>(
    gql`
      query mySleeves {
        data: mySleeves {
          ${PLAYER_SLEEVES_FIELDS}
        }
      }
    `
  );

/** Elige la funda propia entre las disponibles; null se la saca. */
export const setMySleeve = async (
  user: Nullable<UserWithToken>,
  sleeveId: Nullable<number>
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation setMySleeve($sleeveId: Int) {
        data: setMySleeve(sleeveId: $sleeveId)
      }
    `,
    { sleeveId }
  );

/** Las que puede elegir un jugador (con los motivos), y la elegida. Admin. */
export const getUserSleeves = async (user: Nullable<UserWithToken>, userId: number) =>
  getGraphQLQueryClient(user).request<WithData<PlayerSleeves>>(
    gql`
      query userSleeves($userId: Int!) {
        data: userSleeves(userId: $userId) {
          ${PLAYER_SLEEVES_FIELDS}
        }
      }
    `,
    { userId }
  );

/** Otorgarla de nuevo actualiza el motivo. Admin. */
export const grantSleeve = async (
  user: Nullable<UserWithToken>,
  userId: number,
  sleeveId: number,
  reason?: Nullable<string>
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation grantSleeve($userId: Int!, $sleeveId: Int!, $reason: String) {
        data: grantSleeve(userId: $userId, sleeveId: $sleeveId, reason: $reason)
      }
    `,
    { userId, sleeveId, reason: reason ?? null }
  );

/** Si la tenía elegida y no es pública, se queda sin funda propia. Admin. */
export const revokeSleeve = async (
  user: Nullable<UserWithToken>,
  userId: number,
  sleeveId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation revokeSleeve($userId: Int!, $sleeveId: Int!) {
        data: revokeSleeve(userId: $userId, sleeveId: $sleeveId)
      }
    `,
    { userId, sleeveId }
  );

/** La del jugador en ese torneo: pisa la del torneo y la suya; null la saca. Admin. */
export const setTournamentPlayerSleeve = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  userId: number,
  sleeveId: Nullable<number>
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation setTournamentPlayerSleeve($tournamentId: Int!, $userId: Int!, $sleeveId: Int) {
        data: setTournamentPlayerSleeve(
          tournamentId: $tournamentId
          userId: $userId
          sleeveId: $sleeveId
        )
      }
    `,
    { tournamentId, userId, sleeveId }
  );
